// src/components/Widgets/KilnTrackerWidget.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../../lib/nav";
import WidgetHeader from "../Common/WidgetHeader";
import { useStorage } from "../../contexts/StorageContext";
import {
  clayFireTypeLabel,
  clayFireTypeTagClass,
  clayFilterOptions,
  inferClayFireType,
  matchesClayFilter,
} from "../Kiln/kilnFireTypes";

const LS_KEY = "mudoKilnLogs";
const FILE_NAME = "kiln/kilnLogs.json"; // match KilnLogsPage

/* ---------- helpers ---------- */
const readLS = () => {
  try {
    const v = localStorage.getItem(LS_KEY);
    const parsed = v ? JSON.parse(v) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const writeLS = (logs) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(logs));
  } catch {}
};
const clearLS = () => {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {}
};

const parseDate = (v) => {
  const cand = v?.date ?? v?.dateISO ?? v?.when ?? v?.addedAt ?? v?.at ?? v?.ts;
  const t = Date.parse(cand || "");
  return Number.isFinite(t) ? new Date(t) : null;
};
const displayDate = (v) => (parseDate(v) || new Date()).toLocaleDateString();

export default function KilnTrackerWidget({ isDark, backTo = "/dashboard", onOpenModal }) {
  const navigate = useNavigate();
  const { hasFolder, loadJSON, saveJSON, workspaceKey } = useStorage();
  const [logs, setLogs] = useState(() => readLS());
  const [status, setStatus] = useState(""); // inline status line
  const [clayFilter, setClayFilter] = useState("all");

  // Hydrate whenever the workspace changes
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (hasFolder) {
          const disk = await loadJSON(FILE_NAME, null);
          if (!alive) return;
          if (Array.isArray(disk)) {
            setLogs(disk);
            writeLS(disk); // keep LS hot for legacy areas
          } else {
            setLogs([]);
            clearLS();
          }
        } else {
          setLogs(readLS());
        }
      } catch (e) {
        // non-fatal; keep whatever we had
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasFolder, workspaceKey, loadJSON]);

  // Keep in sync if other tabs/screens update
  useEffect(() => {
    const reload = async () => {
      try {
        if (hasFolder) {
          const disk = await loadJSON(FILE_NAME, null);
          if (Array.isArray(disk)) {
            setLogs(disk);
            writeLS(disk);
            return;
          }
          setLogs([]);
          clearLS();
          return;
        }
        setLogs(readLS());
      } catch {}
    };
    window.addEventListener("storage", reload);
    window.addEventListener("mudo:kiln-updated", reload);
    return () => {
      window.removeEventListener("storage", reload);
      window.removeEventListener("mudo:kiln-updated", reload);
    };
  }, [hasFolder, loadJSON]);

  // Persist to folder whenever logs change (noop if no folder)
  useEffect(() => {
    (async () => {
      try {
        if (hasFolder) {
          await saveJSON(FILE_NAME, logs);
          setStatus("Saved");
          setTimeout(() => setStatus(""), 1200);
        }
      } catch {}
    })();
  }, [logs, hasFolder, saveJSON]);

  const logsWithClay = useMemo(
    () => (logs || []).map((log) => ({ ...log, clayFireType: inferClayFireType(log) })),
    [logs]
  );

  const sortedLogs = useMemo(
    () =>
      [...logsWithClay].sort((a, b) => {
        const ta = parseDate(a)?.getTime() ?? 0;
        const tb = parseDate(b)?.getTime() ?? 0;
        return tb - ta;
      }),
    [logsWithClay]
  );

  const stats = useMemo(() => {
    if (!logsWithClay.length) return { count: 0, thisMonth: 0, totalHours: 0 };

    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    const isThisMonth = (entry) => {
      const d = parseDate(entry);
      return d && d.getFullYear() === y && d.getMonth() === m;
    };

    const thisMonthLogs = logsWithClay.filter(isThisMonth);
    const totalHours = thisMonthLogs.reduce(
      (sum, l) => sum + (Number(l.durationHours ?? l.hours ?? 0) || 0),
      0
    );

    return { count: logsWithClay.length, thisMonth: thisMonthLogs.length, totalHours };
  }, [logsWithClay]);

  const filteredRecent = useMemo(() => {
    const base =
      clayFilter === "all" ? sortedLogs : sortedLogs.filter((l) => matchesClayFilter(l, clayFilter));
    return base.slice(0, 3);
  }, [sortedLogs, clayFilter]);

  const card = isDark
    ? "rounded border p-3 bg-white/5 border-white/10"
    : "rounded border p-3 bg-white border-zinc-200";
  const label = "text-[11px] opacity-70";

  return (
    <div className="p-0 rounded-lg bg-transparent text-inherit space-y-3">
      <WidgetHeader title="Kiln Tracker" to={backTo} />

      {/* Inline status */}
      {status && <div className="text-[11px] opacity-70 -mt-2">{status}</div>}

      {/* Clay fire type filter */}
      <div className="flex flex-wrap gap-2 text-xs">
        {clayFilterOptions.map((opt) => {
          const active = clayFilter === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setClayFilter(opt.value)}
              className={`px-2 py-1 rounded-full border transition ${
                active
                  ? "bg-orange-500/20 border-orange-400 text-orange-200"
                  : "border-white/20 text-white/70 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className={card}>
          <div className={label}>Total Logs</div>
          <div className="text-xl font-semibold tabular-nums">{stats.count}</div>
        </div>
        <div className={card}>
          <div className={label}>This Month</div>
          <div className="text-xl font-semibold tabular-nums">{stats.thisMonth}</div>
        </div>
        <div className={card}>
          <div className={label}>Hours Fired</div>
          <div className="text-xl font-semibold tabular-nums">
            {Number(stats.totalHours || 0)}h
          </div>
        </div>
      </div>

      {/* Recent */}
      <div className={card}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm">Recent</div>
          <button
            onClick={() =>
              navigateTo(navigate, ROUTES.KILN_LOGS, { source: "KilnTrackerWidget:manage" })
            }
            className="text-xs underline opacity-70 hover:opacity-100"
          >
            manage
          </button>
        </div>

        {sortedLogs.length === 0 ? (
          <div className="text-xs opacity-60">No firings yet.</div>
        ) : filteredRecent.length === 0 ? (
          <div className="text-xs opacity-60">No firings for this clay type yet.</div>
        ) : (
          <ul className="space-y-2 text-xs">
            {filteredRecent.map((log) => (
              <li key={log.id} className="flex justify-between items-center">
                <div className="truncate">
                  <span className="font-medium">{displayDate(log)}</span>{" "}
                  <span className="opacity-70">
                    {(log.type || "Firing")} - {(log.temperature ?? log.temp ?? "--")}°C
                  </span>
                  <span className="ml-2">
                    <span className={clayFireTypeTagClass(log.clayFireType || "other")}>
                      {clayFireTypeLabel(log.clayFireType || "other")}
                    </span>
                  </span>
                </div>
                <span className="shrink-0 opacity-80 tabular-nums">
                  {Number(log.durationHours ?? log.hours ?? 0)}h
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={() =>
            onOpenModal
              ? onOpenModal()
              : navigateTo(navigate, ROUTES.KILN_LOGS_NEW, {
                  source: "KilnTrackerWidget:logFiring",
                })
          }
          className="px-3 py-1 rounded text-sm text-white transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          + Log Firing
        </button>
        <button
          onClick={() =>
            navigateTo(navigate, ROUTES.KILN_LOGS, { source: "KilnTrackerWidget:viewAll" })
          }
          className="px-3 py-1 rounded text-sm text-white"
          style={{ background: "var(--color-secondary)" }}
        >
          View All
        </button>
      </div>
    </div>
  );
}
